import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { X, Calculator, Building2, Tag, AlertCircle, ChevronRight, Check } from 'lucide-react';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';
import api from '../lib/api';
import { getEligibleDistributors } from '../lib/eligibility';

/**
 * QuickAddModal — Phase 4I (Stabilisation finale)
 * Uses centralized eligibility from lib/eligibility.js
 */
const QuickAddModal = ({ product, open, onClose }) => {
  const { addItem } = useCart();
  const [step, setStep] = useState(1);
  const [selectedDist, setSelectedDist] = useState(null);
  const [qty, setQty] = useState(1);
  const [qtyError, setQtyError] = useState('');
  const [mechanics, setMechanics] = useState([]);
  const [loadingMech, setLoadingMech] = useState(false);
  const [selectedMechId, setSelectedMechId] = useState(undefined);
  const [adding, setAdding] = useState(false);

  const eligibleDists = getEligibleDistributors(product);

  // RESET COMPLET à chaque ouverture — aucune sélection ne survit
  useEffect(() => {
    if (open) {
      setStep(1);
      setSelectedDist(null);
      setQty(1);
      setQtyError('');
      setMechanics([]);
      setLoadingMech(false);
      setSelectedMechId(undefined);
      setAdding(false);
    }
  }, [open]);

  const loadMechanics = useCallback(async (dist) => {
    if (!dist?.id || !product?.id) return;
    setLoadingMech(true);
    try {
      const res = await api.get(`/products/${product.id}/distributions/${dist.id}/available-mechanics`);
      setMechanics((res.data?.mechanics || []).filter(m => m.status === 'ACTIVE'));
    } catch {
      setMechanics([]);
    }
    setLoadingMech(false);
  }, [product?.id]);

  // Step 1 → Step 2 : select distributor
  const selectDistributor = (dist) => {
    setSelectedDist(dist);
    const minQ = dist.minimum_commande || 1;
    const mult = dist.multiple_commande || 1;
    setQty(Math.max(minQ, mult, 1));
    setQtyError('');
    setSelectedMechId(undefined);
    setMechanics([]);
    loadMechanics(dist);
    setStep(2);
  };

  const minQty = selectedDist?.minimum_commande || 1;
  const multiple = selectedDist?.multiple_commande || 1;
  const maxQty = selectedDist?.maximum_commande;

  const validateQty = (v) => {
    const n = parseInt(v) || 0;
    if (n < minQty) return `Minimum : ${minQty} unit\u00e9s`;
    if (maxQty && n > maxQty) return `Maximum : ${maxQty} unit\u00e9s`;
    if (multiple > 1 && n % multiple !== 0) return `Multiple de ${multiple} requis`;
    return '';
  };

  const handleQtyChange = (e) => {
    const v = parseInt(e.target.value) || 0;
    setQty(v);
    setQtyError(validateQty(v));
  };

  // Step 2 → Step 3 : validate qty then go to offers
  const goToOffers = () => {
    const err = validateQty(qty);
    if (err) { setQtyError(err); return; }
    setStep(3);
  };

  // Step 3 : select offer then add
  const selectOfferAndAdd = async (mechId) => {
    setSelectedMechId(mechId);
    const err = validateQty(qty);
    if (err) { setQtyError(err); return; }
    if (!selectedDist) return;
    setAdding(true);
    const ok = await addItem({
      product_id: product.id,
      product_name: product.intitule,
      product_image: (product.visuels || [])[0],
      distribution_id: selectedDist.id,
      partner_id: selectedDist.partner_id,
      partner_nom: selectedDist.partner_nom || 'Distributeur',
      base_price: selectedDist.purchase_price || 0,
      quantity: qty,
      min_qty: minQty,
      max_qty: maxQty,
      multiple,
      mechanic_id: mechId,
    });
    setAdding(false);
    if (ok) handleClose();
  };

  // Close handler — stops propagation to prevent navigation
  const handleClose = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    onClose();
  };

  if (!open || !product) return null;

  const STEPS = [
    { n: 1, label: 'Distributeur' },
    { n: 2, label: 'Quantit\u00e9' },
    { n: 3, label: 'Offre' },
  ];

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      onClick={handleClose} onMouseDown={e => e.stopPropagation()}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      <div className="relative w-full sm:max-w-md bg-white sm:rounded-xl rounded-t-xl shadow-2xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()} data-testid="quick-add-modal">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-3 min-w-0">
            {product.visuels?.[0] && (
              <img src={product.visuels[0]} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
            )}
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-slate-900 truncate">{product.intitule}</h3>
              <p className="text-[10px] text-slate-500">Ajouter à la simulation</p>
            </div>
          </div>
          <button onClick={handleClose} className="p-1.5 rounded-lg hover:bg-slate-200 transition-colors" data-testid="quick-add-close">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        {/* Steps indicator */}
        <div className="flex items-center gap-1 px-5 py-2.5 bg-white border-b border-slate-100">
          {STEPS.map(({ n, label }) => (
            <React.Fragment key={n}>
              {n > 1 && <ChevronRight className="h-3 w-3 text-slate-300 flex-shrink-0" />}
              <button
                onClick={() => { if (n < step) setStep(n); }}
                className={`text-[11px] font-medium px-2 py-1 rounded-md transition-colors ${
                  step === n ? 'bg-indigo-100 text-indigo-700' :
                  step > n ? 'text-indigo-600 hover:bg-slate-100 cursor-pointer' :
                  'text-slate-400'
                }`}
                disabled={n > step}
              >
                {step > n && <Check className="h-3 w-3 inline mr-0.5" />}
                {label}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">

          {/* STEP 1 — Distributeur */}
          {step === 1 && (
            <div className="space-y-2" data-testid="step-distributor">
              <p className="text-xs font-semibold text-slate-600 mb-3">Choisir un distributeur</p>
              {eligibleDists.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-6">Aucun distributeur partenaire commercial disponible</p>
              ) : (
                eligibleDists.map(dist => (
                  <button key={dist.id} onClick={() => selectDistributor(dist)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/30 transition-all text-left"
                    data-testid={`dist-option-${dist.id?.substring(0, 8)}`}>
                    <Building2 className="h-5 w-5 text-slate-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{dist.partner_nom}</p>
                      <p className="text-xs text-slate-500">
                        {dist.purchase_price?.toFixed(2)}&euro; HT
                        {dist.minimum_commande > 1 && <span className="ml-2">Min: {dist.minimum_commande}</span>}
                        {dist.multiple_commande > 1 && <span className="ml-2">Multiple: {dist.multiple_commande}</span>}
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0" />
                  </button>
                ))
              )}
            </div>
          )}

          {/* STEP 2 — Quantit&eacute; (AVANT offre — l'offre d&eacute;pend du volume) */}
          {step === 2 && (
            <div className="space-y-4" data-testid="step-quantity">
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 flex items-center gap-2">
                <Building2 className="h-4 w-4 text-slate-400 flex-shrink-0" />
                <span className="text-sm font-semibold text-slate-700">{selectedDist?.partner_nom}</span>
                <span className="ml-auto text-xs text-slate-500 font-mono">{selectedDist?.purchase_price?.toFixed(2)}&euro;/u</span>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Quantit&eacute;</label>
                <input
                  type="number" min={minQty} step={multiple} value={qty}
                  onChange={handleQtyChange}
                  autoFocus
                  className={`w-full h-12 text-center text-lg font-bold border-2 rounded-xl tabular-nums transition-colors focus:outline-none ${
                    qtyError ? 'border-red-300 focus:border-red-500 bg-red-50/30' : 'border-slate-200 focus:border-indigo-500'
                  }`}
                  data-testid="quick-add-qty"
                />
                {qtyError && (
                  <p className="flex items-center gap-1.5 mt-2 text-xs text-red-600" data-testid="quick-add-qty-error">
                    <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" /> {qtyError}
                  </p>
                )}
                <div className="flex gap-3 mt-2 text-[10px] text-slate-400">
                  {minQty > 1 && <span>Min: {minQty}</span>}
                  {multiple > 1 && <span>Multiple: {multiple}</span>}
                  {maxQty && <span>Max: {maxQty}</span>}
                </div>
              </div>

              <Button onClick={goToOffers} disabled={!!qtyError || qty < 1}
                className="w-full h-10 gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-xl disabled:opacity-50"
                data-testid="step-qty-next">
                Choisir une offre <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* STEP 3 — Offre (apr&egrave;s quantit&eacute; — les m&eacute;caniques d&eacute;pendent du volume) */}
          {step === 3 && (
            <div className="space-y-2" data-testid="step-offer">
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs">
                  <Building2 className="h-3.5 w-3.5 text-slate-400" />
                  <span className="font-semibold text-slate-700">{selectedDist?.partner_nom}</span>
                </div>
                <span className="text-xs font-bold text-indigo-700 tabular-nums">{qty} unit&eacute;{qty > 1 ? 's' : ''}</span>
              </div>

              <p className="text-xs font-semibold text-slate-600 mb-1">Choisir une offre</p>

              {loadingMech ? (
                <div className="flex items-center justify-center py-6 gap-2 text-sm text-slate-400">
                  <div className="animate-spin h-4 w-4 border-2 border-slate-400 border-t-transparent rounded-full" />
                </div>
              ) : mechanics.length === 0 ? (
                /* Cas B : aucune offre commerciale — ajout au prix standard */
                <div className="space-y-3" data-testid="no-offers-available">
                  <p className="text-xs text-slate-500 text-center py-2">Aucune offre commerciale disponible pour cette quantit&eacute;</p>
                  <button onClick={() => selectOfferAndAdd(null)}
                    disabled={adding}
                    className="w-full flex items-center justify-center gap-2 p-3 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition-colors disabled:opacity-50"
                    data-testid="offer-none">
                    {adding ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : <Calculator className="h-4 w-4" />}
                    Ajouter
                  </button>
                </div>
              ) : (
                /* Cas A : offres disponibles */
                <>
                  {/* Sans offre — prix standard */}
                  <button onClick={() => selectOfferAndAdd(null)}
                    disabled={adding}
                    className="w-full flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/30 transition-all text-left disabled:opacity-50"
                    data-testid="offer-none">
                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Calculator className="h-4 w-4 text-slate-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-700">Ajouter sans offre</p>
                      <p className="text-xs text-slate-400">Prix standard : {selectedDist?.purchase_price?.toFixed(2)}&euro; HT</p>
                    </div>
                  </button>

                  {/* Mécaniques commerciales */}
                  {mechanics.map(m => (
                    <button key={m.id} onClick={() => selectOfferAndAdd(m.id)}
                      disabled={adding}
                      className="w-full flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/30 transition-all text-left disabled:opacity-50"
                      data-testid={`offer-${m.id?.substring(0, 8)}`}>
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                        <Tag className="h-4 w-4 text-indigo-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">{m.title || 'Offre'}</p>
                        <p className="text-xs text-slate-500">
                          {m.nature_offre || m.effect_type}
                          {m.pricing_rules?.length > 0 && ` \u2014 ${m.pricing_rules.length} palier(s)`}
                        </p>
                      </div>
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default QuickAddModal;
